import { Flex, Box, Icon } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { Check } from '@kula-ai/icons-core';

export interface KStepItem {
  id?: string | number;
  label: string;
  status: 'active' | 'pending' | 'completed';
  enabled?: boolean;
  labelProps?: any;
}

export interface KStepsProps {
  steps?: KStepItem[];
  onSelect?: ({ step, index }: { step: KStepItem; index: number }) => void;
}

export const KSteps = ({ steps, onSelect }: KStepsProps) => {
  const STEP_STATUS_ACTIVE = 'active';
  const STEP_STATUS_PENDING = 'pending';
  const STEP_STATUS_COMPLETED = 'completed';

  const GET_STATUS_BLOCK = (status, index) => {
    const PENDING_BLOCK = (
      <Box
        border="1px solid"
        borderColor="secondary-500"
        w={'21px'}
        h={'21px'}
        borderRadius={'50%'}
        fontWeight={'medium'}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        mr="10px"
      >
        <KText color={'secondary-500'} fontSize={'14px'} fontWeight={'medium'}>
          {index + 1}
        </KText>
      </Box>
    );
    const ACTIVE_BLOCK = (
      <Box position={'relative'}>
        <Box
          w={'30px'}
          h={'30px'}
          border="2px solid"
          borderColor={'primary-400'}
          bg={'var(--kula-colors-white, #fff)'}
          borderRadius={'50%'}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          mr="10px"
          clipPath="polygon(6% 100%, 100% 100%, 100% 0%, 50% 0%)"
        ></Box>
        <Box
          w={'22px'}
          h={'22px'}
          top={'4px'}
          right={'4px'}
          bg={'primary-400'}
          borderRadius={'50%'}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          mr="10px"
          position={'absolute'}
        >
          <KText color={'#fff'} fontSize={'14px'} fontWeight={'semiBold'}>
            {index + 1}
          </KText>
        </Box>
      </Box>
    );
    const COMPLETED_BLOCK = (
      <Box
        border="1.2px solid"
        borderColor="primary-500"
        bg="secondary-100"
        w={'21px'}
        h={'21px'}
        borderRadius={'50%'}
        fontWeight={'medium'}
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        mr="10px"
      >
        <Icon
          // @ts-expect-error - icon is a valid IconType
          as={Check}
          width="12px"
          height="12px"
          color="primary-500"
        />
      </Box>
    );

    const STEP_COMPONENT_HASH = {
      [STEP_STATUS_ACTIVE]: ACTIVE_BLOCK,
      [STEP_STATUS_PENDING]: PENDING_BLOCK,
      [STEP_STATUS_COMPLETED]: COMPLETED_BLOCK,
    };

    return STEP_COMPONENT_HASH[status] || PENDING_BLOCK;
  };

  return (
    <Flex>
      {steps.map((step, index: number) => {
        const { enabled = true, status, label, labelProps }: KStepItem = step;
        return (
          <Flex
            cursor={enabled ? 'pointer' : 'not-allowed'}
            key={index}
            onClick={() =>
              enabled &&
              onSelect?.({
                step,
                index,
              })
            }
            alignItems={'center'}
            opacity={enabled ? 1 : 0.5}
          >
            {GET_STATUS_BLOCK(status, index)}
            <KText
              mr="10px"
              ml="-5px"
              color={
                status === STEP_STATUS_PENDING
                  ? 'secondary-700'
                  : 'secondary-900'
              }
              fontWeight={status === STEP_STATUS_PENDING ? 'regular' : 'medium'}
              data-test-id={`tab-step-${label}`}
              {...labelProps}
            >
              {label}
            </KText>
            {index < steps.length - 1 && (
              <Box
                mr="15px"
                w="75px"
                h="1px"
                bg={
                  status === STEP_STATUS_COMPLETED
                    ? 'primary-500'
                    : 'secondary-200'
                }
              ></Box>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};

KSteps.displayName = 'KSteps';
